/**
 * GatewayPicker.jsx — region-aware payment-gateway selector.
 *
 * Renders two radio-style chips (primary + backup) for the matrix
 * defined in backend/payment_routing.py. When the parent supplies a
 * `baseGbp` prop the picker also shows the actual charge amount per
 * pill, plus a "Best Price" / "Saves £X" badge on the cheaper option.
 *
 * Optional "Foreign-issued card" toggle on the NG row re-routes the
 * matrix to Nigerian-diaspora (Stripe primary + Paystack-intl backup).
 *
 * Props:
 *   onChange        (required) — { provider, currency, is_diaspora_card, region, tier }
 *   baseGbp         (optional, number) — enables per-pill price quotes + best-price badge
 *   regionOverride  (optional)         — force a region (admin/testing)
 *   compact         (optional, bool)   — render in a single row instead of stacked
 *   hideDiasporaToggle (optional)
 */
import React, { useEffect, useState } from 'react';
import {
  CreditCard, ShieldCheck, Globe, Wallet, Loader2,
  Smartphone, Zap, AlertTriangle,
} from 'lucide-react';
import { PAYMENTS_DISABLED, PAYMENTS_UNAVAILABLE_MESSAGE } from '../utils/paymentAvailability';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const PROVIDER_LABEL = {
  // `wallets` controls whether the pill shows the "Apple Pay · Google Pay"
  // mini-badge. Stripe Checkout enables both wallets automatically on
  // supported devices (no API config required); Feb 12 2026 user spec
  // surfaced them in the UI so buyers know the option exists.
  stripe:      { name: 'Stripe',      icon: CreditCard,  accent: 'text-indigo-300', tint: 'border-indigo-400/60 bg-indigo-500/15', wallets: true  },
  paypal:      { name: 'PayPal',      icon: Wallet,      accent: 'text-sky-300',    tint: 'border-sky-400/60 bg-sky-500/15',       wallets: false },
  paystack:    { name: 'Paystack',    icon: ShieldCheck, accent: 'text-emerald-300',tint: 'border-emerald-400/60 bg-emerald-500/15', wallets: false },
  flutterwave: { name: 'Flutterwave', icon: Globe,       accent: 'text-amber-300',  tint: 'border-amber-400/60 bg-amber-500/15',     wallets: false },
  sumup:       { name: 'SumUp',       icon: Smartphone,  accent: 'text-rose-300',   tint: 'border-rose-400/60 bg-rose-500/15',       wallets: true  },
};

const CURRENCY_SYMBOL = {
  GBP: '£', USD: '$', EUR: '€', NGN: '₦', KES: 'KSh',
};

const REGION_LABEL = {
  uk: 'UK',
  eu: 'EU',
  us: 'US',
  nigeria: 'Nigeria',
  kenya: 'Kenya / East Africa',
  africa: 'Africa',
  rest_of_world: 'Rest of world',
  nigeria_diaspora: 'Nigerian diaspora (foreign card)',
};

const formatAmount = (amt, currency) => {
  if (amt == null) return '';
  const sym = CURRENCY_SYMBOL[currency] || '';
  if (currency === 'NGN' || currency === 'KES') {
    return `${sym}${Number(amt).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  }
  return `${sym}${Number(amt).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Hoisted out of the parent component to satisfy
// react/no-unstable-nested-components — receives all state via props.
const Pill = ({
  tierKey, data, quote, isOn, isConfigured, fee, onPick, isBest, savingsLabel,
}) => {
  const meta = PROVIDER_LABEL[data.provider] || PROVIDER_LABEL.stripe;
  const Icon = meta.icon;
  return (
    <button
      type="button"
      onClick={() => isConfigured && onPick(tierKey)}
      disabled={!isConfigured}
      className={`relative w-full text-left rounded-xl border px-3 py-3 transition-all duration-200 active:scale-[0.98] ${
        isOn
          ? `${meta.tint} ring-1 ring-white/20 shadow-lg`
          : 'bg-slate-900/40 border-slate-700 hover:border-slate-500'
      } ${!isConfigured ? 'opacity-50 cursor-not-allowed' : ''}`}
      data-testid={`gateway-pill-${data.provider}`}
    >
      {/* "Best Price" badge — sits at the top-right of whichever pill the
          backend flagged as `cheapest`. Hidden when there's no `baseGbp`. */}
      {isBest && isConfigured && (
        <span
          className="absolute -top-2 right-3 inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-400 text-slate-900 shadow-md shadow-emerald-500/30"
          data-testid={`gateway-best-price-${data.provider}`}
        >
          <Zap className="w-2.5 h-2.5" /> Best Price
        </span>
      )}
      {/* "Setup needed" warning — replaces the small grey label with a
          high-contrast amber chip so the merchant can't miss missing keys. */}
      {!isConfigured && (
        <span
          className="absolute -top-2 right-3 inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-400 text-slate-900 shadow-md"
          data-testid={`gateway-setup-needed-${data.provider}`}
        >
          <AlertTriangle className="w-2.5 h-2.5" /> Setup needed
        </span>
      )}
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${meta.accent}`} />
        <span className="text-sm font-bold text-white">{meta.name}</span>
        <span className={`text-[10px] uppercase tracking-widest font-extrabold ml-auto ${
          tierKey === 'primary' ? 'text-emerald-300' : 'text-slate-400'
        }`}>
          {tierKey === 'primary' ? 'Recommended' : 'Backup'}
        </span>
      </div>
      <div className="mt-1 flex items-center justify-between">
        {/* Actual amount when a quote is available; otherwise just the currency tag. */}
        {quote ? (
          <span className={`text-base font-extrabold tabular-nums ${meta.accent}`}>
            {formatAmount(quote.amount, quote.currency)}
          </span>
        ) : (
          <span className={`text-[11px] font-semibold ${meta.accent}`}>
            Pays in {CURRENCY_SYMBOL[data.currency] || ''}{data.currency}
          </span>
        )}
        {fee && (
          <span className="text-[10px] text-slate-500 truncate ml-2 max-w-[60%] text-right">{fee}</span>
        )}
      </div>
      {/* Sub-line: shows the FX'd currency (when we display a quote) +
          a "Saves £X" badge when this pill is the cheapest. */}
      {(quote || savingsLabel) && (
        <div className="mt-1 flex items-center justify-between">
          {quote && quote.currency !== 'GBP' && (
            <span className="text-[10px] text-slate-500 uppercase tracking-wider">
              {quote.currency}
            </span>
          )}
          {savingsLabel && (
            <span className="ml-auto text-[10px] font-bold text-emerald-300 tabular-nums">
              {savingsLabel}
            </span>
          )}
        </div>
      )}
      {/* Apple Pay / Google Pay mini-badge — Feb 12 2026. Stripe (and
          SumUp Tap-to-Pay) auto-detect wallet availability at the hosted
          checkout; this badge tells the buyer up front they can pay with
          a single tap on their phone. */}
      {meta.wallets && (
        <div
          className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-black/40 border border-white/10 px-2 py-0.5"
          data-testid={`gateway-wallets-badge-${data.provider}`}
        >
          <Smartphone className="w-3 h-3 text-white/70" />
          <span className="text-[9px] font-bold tracking-widest text-white/70 uppercase">
            Apple Pay · Google Pay
          </span>
        </div>
      )}
    </button>
  );
};

const GatewayPicker = ({
  onChange,
  baseGbp,
  regionOverride,
  compact = false,
  hideDiasporaToggle = false,
}) => {
  const [opts, setOpts] = useState(null);
  const [error, setError] = useState('');
  const [tier, setTier] = useState('primary');
  const [isDiaspora, setIsDiaspora] = useState(false);

  // Refetch whenever any prop affecting routing/quotes changes.
  useEffect(() => {
    if (PAYMENTS_DISABLED) return undefined;
    let alive = true;
    setOpts(null);
    setError('');
    const qs = new URLSearchParams();
    if (regionOverride) qs.set('region', regionOverride);
    if (isDiaspora) qs.set('diaspora', '1');
    if (baseGbp != null && Number(baseGbp) > 0) qs.set('base_gbp', String(baseGbp));
    const url = `${BACKEND_URL}/api/payments/gateway-options${qs.toString() ? '?' + qs.toString() : ''}`;
    (async () => {
      try {
        const res = await fetch(url, { credentials: 'include' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!alive) return;
        setOpts(data);
        // Iter 64 — auto-select the cheapest tier instead of always
        // defaulting to primary. This wires the "Best Price" suggestion
        // straight into the default user choice (still toggleable).
        setTier((data && data.cheapest) || 'primary');
      } catch (e) {
        if (alive) setError(typeof e?.message === 'string' ? e.message : 'Failed to load gateway options');
      }
    })();
    return () => { alive = false; };
  }, [regionOverride, isDiaspora, baseGbp]);

  // Bubble selection up to the parent every time anything changes.
  useEffect(() => {
    if (!opts) return;
    const choice = opts[tier];
    if (!choice) return;
    onChange?.({
      provider: choice.provider,
      currency: choice.currency,
      is_diaspora_card: isDiaspora,
      region: opts.region,
      tier,
    });
  }, [opts, tier, isDiaspora, onChange]);

  if (PAYMENTS_DISABLED) {
    return (
      <p
        className="rounded-lg border border-amber-300/35 bg-amber-400/10 px-3 py-2 text-xs font-semibold text-amber-100"
        role="status"
        data-testid="gateway-picker-payments-disabled"
      >
        {PAYMENTS_UNAVAILABLE_MESSAGE}
      </p>
    );
  }

  if (error) {
    return (
      <p className="text-xs text-rose-300" data-testid="gateway-picker-error">
        Couldn&apos;t load payment options: {error}
      </p>
    );
  }
  if (!opts) {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-400 py-2" data-testid="gateway-picker-loading">
        <Loader2 className="w-3 h-3 animate-spin" /> Loading payment options…
      </div>
    );
  }

  const primary = opts.primary;
  const backup = opts.backup;
  const configured = opts.configured || {};
  const fees = opts.fee_hints || {};
  const quotes = opts.quotes || {};
  const cheapest = opts.cheapest;
  const savings = opts.savings_gbp;
  const savingsLabel = savings && savings > 0 ? `Saves £${Number(savings).toFixed(2)}` : null;
  const showDiasporaToggle = !hideDiasporaToggle && (opts.region === 'nigeria' || opts.region === 'nigeria_diaspora');

  return (
    <div data-testid="gateway-picker-root" className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-[0.3em] font-extrabold text-slate-400">
          Pay with · {REGION_LABEL[opts.region] || opts.region}
        </p>
        {showDiasporaToggle && (
          <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-300 cursor-pointer" data-testid="gateway-diaspora-toggle">
            <input
              type="checkbox"
              className="h-3 w-3 accent-amber-400"
              checked={isDiaspora}
              onChange={(e) => setIsDiaspora(e.target.checked)}
              data-testid="gateway-diaspora-checkbox"
            />
            Foreign-issued card
          </label>
        )}
      </div>
      <div className={`grid ${compact ? 'grid-cols-2' : 'grid-cols-1 sm:grid-cols-2'} gap-2`}>
        {/* Iter 64 — wire the "Best Price" suggestion: when the backend
            marks the backup as cheapest, render it first so users land
            on the cheaper option by default. Primary stays "Recommended"
            label-wise; the badge does the highlighting. */}
        {(cheapest === 'backup' ? ['backup', 'primary'] : ['primary', 'backup']).map((slot) => {
          const data = slot === 'primary' ? primary : backup;
          const quote = quotes[slot];
          const fee = fees[data.provider];
          return (
            <Pill
              key={slot}
              tierKey={slot}
              data={data}
              quote={quote}
              isOn={tier === slot}
              isConfigured={configured[data.provider] !== false}
              fee={fee}
              onPick={setTier}
              isBest={cheapest === slot && configured[data.provider] !== false}
              savingsLabel={cheapest === slot ? savingsLabel : null}
            />
          );
        })}
      </div>
      {/* Apple/Google Pay reassurance — Stripe handles these automatically
          when wallets are toggled on in the dashboard, so we just surface
          a quiet line so users know they exist on the recommended path. */}
      {(primary.provider === 'stripe' || backup.provider === 'stripe') && (
        <p className="text-[10px] text-slate-500 mt-1" data-testid="gateway-wallets-note">
          Stripe accepts Apple Pay · Google Pay · Link
        </p>
      )}
    </div>
  );
};

export default GatewayPicker;
